import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { ChevronLeft, ChevronRight, FileText, RefreshCw } from 'lucide-react';

function LogIngestManagePage() {
  const [logs, setLogs] = useState([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);
  const [loading, setLoading] = useState(false);

  const fetchLogs = async (overrides = {}) => {
    const activePage = overrides.page ?? page;
    const activeLimit = overrides.limit ?? limit;

    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/rag-documents/logs`, {
        params: {
          start: activePage * activeLimit,
          limit: activeLimit,
        },
        withCredentials: true,
      });
      setLogs(response.data.data || []);
      setTotalLogs(response.data.recordsFiltered || 0);
    } catch (error) {
      console.error(error);
      // Only show alert if it's not from polling
      if (!overrides.silent) {
        Swal.fire('Oops!', error.response?.data?.message || 'Gagal memuat Logs.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page, limit]);

  useEffect(() => {
    // Poll logs only on the first page
    if (page !== 0) return;

    const timer = setInterval(() => {
      fetchLogs({ page: 0, limit, silent: true });
    }, 5000);
    return () => clearInterval(timer);
  }, [page, limit]);

  const handleLimitChange = (event) => {
    const newLimit = Number(event.target.value);
    setLimit(newLimit);
    setPage(0);
  };

  const getLogBadge = (status) => {
    const normalized = String(status || 'info').toLowerCase();
    if (normalized === 'completed' || normalized === 'success') return 'bg-success';
    if (normalized === 'failed') return 'bg-danger';
    if (normalized === 'rate_limited') return 'bg-warning text-dark';
    if (normalized === 'queued' || normalized === 'started') return 'bg-primary';
    return 'bg-secondary';
  };

  return (
    <div className="container-fluid px-0">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-4 d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div className="flex items-center gap-2 text-slate-800">
            <FileText size={18} />
            <h4 className="mb-0 text-lg font-semibold">Data Log Ingest</h4>
          </div>
          <button className="btn btn-dark d-inline-flex align-items-center gap-2" onClick={() => fetchLogs()} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        <div className="mb-4 d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div className="d-flex align-items-center gap-2">
            <span className="small text-muted">Tampilkan</span>
            <select className="form-select form-select-sm rounded-lg border-slate-300" style={{ width: '88px' }} value={limit} onChange={handleLimitChange}>
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            <span className="small text-muted">data per halaman</span>
          </div>
        </div>

        <div className="table-responsive rounded-xl border border-slate-300 bg-white mb-3">
          <table className="table table-hover table-striped table-bordered table-sm align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Stage</th>
                <th>Doc ID</th>
                <th>Status</th>
                <th>Message</th>
                <th>API Key / Provider</th>
                <th>Waktu</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center text-muted py-4">
                    {loading ? 'Memuat data...' : 'Belum ada log.'}
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id}>
                    <td className="fw-semibold">{log.stage}</td>
                    <td>{log.document_id ? `#${log.document_id}` : '-'}</td>
                    <td>
                      <span className={`badge ${getLogBadge(log.status)}`}>{String(log.status || '').toUpperCase()}</span>
                    </td>
                    <td style={{ maxWidth: '300px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                      <span className="small">{log.message || '-'}</span>
                    </td>
                    <td>
                      <span className="small text-muted">{log.api_key_name || '-'}</span>
                    </td>
                    <td>
                      <span className="small text-muted">
                        {log.created_at ? new Date(log.created_at).toLocaleString('id-ID') : '-'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex justify-content-start align-items-center gap-3 text-sm text-slate-500">
          <small>Total: {totalLogs}</small>
          <div className="d-inline-flex align-items-center gap-2 rounded-pill border border-slate-200 bg-white px-2 py-1 shadow-sm">
            <button
              className="btn btn-sm btn-link text-decoration-none text-dark p-1 d-flex align-items-center"
              disabled={page === 0 || loading}
              onClick={() => setPage((prev) => prev - 1)}
              aria-label="Halaman sebelumnya"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="px-2 small text-muted">{page + 1} / {Math.ceil(totalLogs / limit) || 1}</span>
            <button
              className="btn btn-sm btn-link text-decoration-none text-dark p-1 d-flex align-items-center"
              disabled={(page + 1) * limit >= totalLogs || loading}
              onClick={() => setPage((prev) => prev + 1)}
              aria-label="Halaman berikutnya"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LogIngestManagePage;
